import { useQuery } from '@tanstack/react-query';
import { EnterpriseSyncApi } from '@api/bff/openapi/endpoints/enterprise-sync-api';
import type { EventDocumentViewModel } from '@api/bff/openapi/models';
import { bffApiClient } from '@api/bff/client';
import { eventDocumentsKeys } from '../constants';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { useOfflineDownloadService } from '@services/offlineDownloadService';

export const useEventDocuments = (
  eventNumber: number | null | undefined,
  enabled = true
) => {
  const isOnline = useOnlineStatus();
  const { getCachedEventDocuments } = useOfflineDownloadService();

  const enterpriseSyncApi = new EnterpriseSyncApi(
    undefined,
    undefined,
    bffApiClient
  );

  return useQuery<EventDocumentViewModel[], Error>({
    queryKey: eventDocumentsKeys.byEventNumber(eventNumber ?? 0),
    queryFn: async () => {
      if (!eventNumber) return [];

      if (!isOnline) {
        return await getCachedEventDocuments(eventNumber);
      }

      try {
        const response =
          await enterpriseSyncApi.apiEnterpriseSyncEventDocumentsEventNumberGet(
            { eventNumber }
          );
        return response.data;
      } catch (error) {
        const cached = await getCachedEventDocuments(eventNumber);
        if (cached.length > 0) {
          return cached;
        }
        throw error;
      }
    },
    enabled: enabled && eventNumber != null && eventNumber > 0,
    staleTime: isOnline ? 30 * 1000 : Infinity,
    gcTime: 5 * 60 * 1000,
    retry: isOnline ? 3 : false
  });
};
